import React, { useState } from "react";
import { Card } from "@/components/ui/card";
import { Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { CompanyHeader } from "./CompanyHeader";
import { CompanyFormWrapper } from "./CompanyFormWrapper";
import { CompanyTableWrapper } from "./CompanyTableWrapper";
import { useCompanies } from "@/hooks/useCompanies";
import type { Company, CompanyFormData } from "@/types/company";

export const CompanySettings = () => {
  const [editingId, setEditingId] = useState<string | null>(null);
  const { companies, isLoading, addCompany, updateCompany } = useCompanies();
  const { toast } = useToast();

  const handleSubmit = async (data: CompanyFormData) => {
    try {
      // Validações
      if (!data.name || !data.email || !data.phone || !data.plan || !data.status || !data.due_date || !data.recurrence) {
        toast({
          variant: "destructive",
          title: "Erro",
          description: "Todos os campos são obrigatórios.",
        });
        return;
      }

      // Validar formato do email
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(data.email)) {
        toast({
          variant: "destructive",
          title: "Erro",
          description: "Email inválido.",
        });
        return;
      }

      // Verificar se já existe uma empresa com o mesmo email
      const existingCompany = companies.find(
        (company) => company.email.toLowerCase() === data.email.toLowerCase() && 
        (!editingId || company.id !== editingId)
      );

      if (existingCompany) {
        toast({
          variant: "destructive",
          title: "Erro",
          description: "Já existe uma empresa com este email.",
        });
        return;
      }

      if (editingId) {
        await updateCompany(editingId, data);
        toast({
          title: "Sucesso",
          description: "Empresa atualizada com sucesso!",
        });
      } else {
        await addCompany(data);
        toast({
          title: "Sucesso",
          description: "Empresa criada com sucesso!",
        });
      }
      handleClear();
    } catch (error: any) {
      console.error('Error saving company:', error);
      toast({
        variant: "destructive",
        title: "Erro",
        description: error.message || "Ocorreu um erro ao salvar a empresa.",
      });
    }
  };

  const handleEdit = (company: Company) => {
    setEditingId(company.id);
  };

  const handleClear = () => {
    setEditingId(null);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CompanyHeader />
        <CompanyFormWrapper
          onSubmit={handleSubmit}
          initialData={editingId ? companies.find((company) => company.id === editingId) : undefined}
          onClear={handleClear}
          isEditing={!!editingId}
        />
      </Card>

      <Card>
        <CompanyTableWrapper companies={companies} onEdit={handleEdit} />
      </Card>
    </div>
  );
};